import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Spin, Card, Input, Select, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import useFormattedDate from "hooks/useFormatDate";
import useActionables from "hooks/userManagement/useUserActionables";
import { useUserManagement } from "hooks";
import ViewActionableDrawer from "./ViewActionableDrawer";
import ActionableFormDrawer from "./ActionableFormDrawer";
import ActionablesTable from "./ActionablesTable";

const UserActionablesPage = () => {
  const { userId } = useParams();
  const { getUserById } = useUserManagement();
  const navigate = useNavigate();
  const { formatDateTime } = useFormattedDate();
  const {
    actionables,
    loading: apiLoading,
    fetchUserActionables,
    createActionable,
    updateActionable,
  } = useActionables();

  const [filters, setFilters] = useState({
    status: "ALL",
    displayType: "ALL",
    urgency: "ALL",
    search: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [selectedActionable, setSelectedActionable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // fetch user
  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const data = await getUserById(userId);
        setUser(data);
      } catch (err) {
        console.log(err.message || "Failed to load user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, getUserById]);

  // fetch actionables
  useEffect(() => {
    const fetchActionables = async () => {
      setLoading(true);
      try {
        await fetchUserActionables(userId);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchActionables();
  }, [userId, fetchUserActionables]);

  // filtering
  const filteredActionables = useMemo(() => {
    return actionables.filter((a) => {
      if (filters.status !== "ALL" && a.status !== filters.status) return false;
      if (filters.urgency !== "ALL" && a.urgency !== filters.urgency)
        return false;
      if (
        filters.displayType !== "ALL" &&
        a.displayType !== filters.displayType
      )
        return false;
      if (
        filters.search &&
        !a.message.toLowerCase().includes(filters.search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [actionables, filters]);

  // stats
  const stats = useMemo(() => {
    return {
      newItems: actionables.filter((a) => a.status === "NEW").length,
      delivered: actionables.filter((a) => a.status === "DELIVERED").length,
      read: actionables.filter((a) => a.status === "READ").length,
      unRead: actionables.filter((a) => a.status === "UNREAD").length,
      highUrgency: actionables.filter(
        (a) => a.urgency === "HIGH" && a.status === "NEW",
      ).length,
    };
  }, [actionables]);

  if (loading || apiLoading)
    return (
      <div className="text-center">
        <Spin size="large" style={{ marginTop: 200 }} />
      </div>
    );

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleView = (actionable) => {
    setSelectedActionable(actionable);
    setDetailsDrawerOpen(true);
  };

  // open drawer for creating
  const handleCreate = () => {
    setSelectedActionable(null);
    setDrawerOpen(true);
  };

  // open drawer for editing
  const handleEdit = (actionable) => {
    setSelectedActionable(actionable);
    setDrawerOpen(true);
  };

  // closing drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedActionable(null);
  };

  // submit
  const handleSubmitActionable = async (actionableData) => {
    // console.log("Actionable", actionableData);
    if (selectedActionable) {
      const payload = {
        message: actionableData.message,
        screen: actionableData.screen,
        url: actionableData.url,
        deepLink: actionableData.deepLink,
      };
      await updateActionable(selectedActionable.id, payload);
    } else {
      await createActionable(actionableData);
    }
  };

  return (
    <div className="mt-3">
      {/* header */}
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button type="default" size="small" onClick={() => navigate(-1)}>
              ←
            </Button>
            <div>
              <h4 className="text-base fw-semibold mb-0 pb-0">{`User Actionables`}</h4>
              <div
                className="fw-semibold"
                style={{ fontSize: 12, color: "#555", fontFamily: "Inter" }}
              >{`${user?.firstName} ${user?.lastName}`}</div>
            </div>
          </Space>
        </Col>
        <Col>
          <Button type="primary" onClick={handleCreate}>
            + Create Actionable
          </Button>
        </Col>
      </Row>

      {/* stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 30 }}>
        {[
          stats.newItems,
          stats.delivered,
          stats.read,
          stats.unRead,
          stats.highUrgency,
        ].map((stat, index) => {
          const labels = ["New", "Delivered", "Read", "Unread", "High Urgency"];
          return (
            <Col
              key={index}
              xs={24}
              sm={12}
              md={8}
              lg={4}
              xl={4}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Card
                size="small"
                style={{
                  textAlign: "center",
                  fontFamily: "Inter",
                  width: "100%",
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 600 }}>{stat}</div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  {labels[index]}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* filters */}
      <div size="small" style={{ marginBottom: 16 }}>
        <div className="d-flex justify-content-between">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            className="search-input"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 280 }}
          />

          <div className="toolbar-actions">
            <Select
              value={filters.status}
              style={{ width: 120 }}
              onChange={(val) => setFilters({ ...filters, status: val })}
              options={[
                { value: "ALL", label: "All Status" },
                { value: "NEW", label: "New" },
                { value: "DELIVERED", label: "Delivered" },
                { value: "READ", label: "Read" },
                { value: "UNREAD", label: "Unread" },
              ]}
            />
            <Select
              value={filters.urgency}
              style={{ width: 120 }}
              onChange={(val) => setFilters({ ...filters, urgency: val })}
              options={[
                { value: "ALL", label: "All Urgency" },
                { value: "LOW", label: "Low" },
                { value: "MEDIUM", label: "Medium" },
                { value: "HIGH", label: "High" },
              ]}
            />
            <Select
              value={filters.displayType}
              style={{ width: 140 }}
              onChange={(val) => setFilters({ ...filters, displayType: val })}
              options={[
                { value: "ALL", label: "All Display" },
                { value: "NOTIFICATION", label: "Notification" },
                { value: "ALERT", label: "Alert" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* table */}
      <ActionablesTable
        actionables={actionables}
        filters={filters}
        pagination={pagination}
        setPagination={setPagination}
        onView={handleView}
        onEdit={handleEdit}
      />

      {/* drawer */}
      <ViewActionableDrawer
        onClose={() => setDetailsDrawerOpen(null)}
        open={detailsDrawerOpen}
        selectedActionable={selectedActionable}
      />

      <ActionableFormDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        actionable={selectedActionable}
        onSubmit={handleSubmitActionable}
        userId={userId}
      />
    </div>
  );
};

export default UserActionablesPage;
